import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import i18n from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { resolveSettings } from '@/lib/coreSettings';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 50. Consommation de la cascade de paramètres
// Les modules ne doivent plus lire des valeurs locales en dur (langue,
// devise, notifications…). Ce provider résout les paramètres effectifs de
// l'organisation (Plateforme → Pays → Entreprise → Établissement →
// Utilisateur) et les expose à toute l'application.
// ─────────────────────────────────────────────────────────────

export interface CoreNotificationsSettings {
  email: boolean;
  push: boolean;
  sms: boolean;
}

export interface CoreSettingsApi {
  loading: boolean;
  ready: boolean;
  settings: Record<string, unknown>;
  /** Valeur brute résolue d'un paramètre (avec repli). */
  get: (key: string, fallback?: unknown) => unknown;
  /** Langue effective (cascade) si définie, sinon null. */
  language: string | null;
  /** Devise effective (cascade) si définie, sinon null. */
  currency: string | null;
  timezone: string | null;
  notifications: CoreNotificationsSettings;
  refresh: () => void;
}

const DEFAULTS: CoreSettingsApi = {
  loading: false,
  ready: false,
  settings: {},
  get: (_key, fallback) => fallback,
  language: null,
  currency: null,
  timezone: null,
  notifications: { email: true, push: false, sms: false },
  refresh: () => {},
};

const CoreSettingsContext = createContext<CoreSettingsApi>(DEFAULTS);

export function CoreSettingsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const commerceId = useMemo(() => getCommerceId(user), [user]);

  const load = useCallback(async () => {
    if (!user) {
      setSettings({});
      setReady(true);
      return;
    }
    setLoading(true);
    try {
      const org = commerceId ? await getOrganizationByCommerce(commerceId) : null;
      const resolved = await resolveSettings({
        organizationId: org?.id ?? null,
        tenantId: org?.tenant_id ?? null,
        country: org?.country ?? null,
        userId: user.id,
      });
      setSettings(resolved);
    } catch {
      setSettings({});
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [user, commerceId]);

  useEffect(() => {
    load();
  }, [load]);

  // Applique la langue résolue par la cascade, SAUF si l'utilisateur a
  // explicitement choisi sa langue (préférence locale mémorisée). La cascade
  // sert alors de valeur par défaut au premier accès.
  useEffect(() => {
    const lang = settings.language;
    if (typeof lang !== 'string' || !lang) return;
    let manual: string | null = null;
    try {
      manual = localStorage.getItem('zifek_lang');
    } catch {
      manual = null;
    }
    if (manual) return;
    if ((i18n.language || '').slice(0, 2) === lang.slice(0, 2)) return;
    i18n.changeLanguage(lang).catch(() => {});
    document.documentElement.setAttribute('lang', lang);
  }, [settings.language]);

  const value = useMemo<CoreSettingsApi>(() => {
    const language = typeof settings.language === 'string' ? (settings.language as string) : null;
    const currency = typeof settings.currency === 'string' ? (settings.currency as string) : null;
    const timezone = typeof settings.timezone === 'string' ? (settings.timezone as string) : null;
    return {
      loading,
      ready,
      settings,
      get: (key: string, fallback?: unknown) => (key in settings ? settings[key] : fallback),
      language,
      currency,
      timezone,
      notifications: {
        email: settings.notifications_email !== false,
        push: settings.notifications_push === true,
        sms: settings.notifications_sms === true,
      },
      refresh: load,
    };
  }, [settings, loading, ready, load]);

  return <CoreSettingsContext.Provider value={value}>{children}</CoreSettingsContext.Provider>;
}

/** Consomme les paramètres effectifs du socle (langue, devise, notifications…). */
export function useCoreSettings(): CoreSettingsApi {
  return useContext(CoreSettingsContext);
}